// Polaris exposes a public health summary at GET /v1/health/ready.
// No auth header — the controller is @Public().

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3500";

export type HealthStatus = "ok" | "degraded" | "down";

export interface HealthService {
  name: string;
  status: HealthStatus;
  latencyMs?: number;
  version?: string;
}

export interface HealthSummary {
  status: HealthStatus;
  gateway: { name: string; version?: string; uptimeSeconds?: number };
  services: HealthService[];
  summary: { total: number; healthy: number; unhealthy: number };
  fetchedAt: string;
}

interface RawService {
  name?: string;
  status?: string;
  latency?: number;
  latencyMs?: number;
  version?: string;
}

interface RawResponse {
  status?: string;
  gateway?: { name?: string; version?: string; uptime?: number };
  services?: RawService[];
  summary?: { total?: number; healthy?: number; unhealthy?: number; unreachable?: number };
}

function normaliseStatus(s: string | undefined): HealthStatus {
  const v = (s ?? "").toLowerCase();
  if (v === "ok" || v === "operational" || v === "healthy" || v === "up") return "ok";
  if (v === "degraded" || v === "warning" || v === "partial") return "degraded";
  return "down";
}

export async function fetchHealthSummary(signal?: AbortSignal): Promise<HealthSummary> {
  const res = await fetch(`${API_URL}/v1/health/ready`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) {
    throw new Error(`Health endpoint returned ${res.status}`);
  }
  const raw = (await res.json()) as RawResponse;
  const services: HealthService[] = (raw.services ?? []).map((s) => ({
    name: s.name ?? "Unknown",
    status: normaliseStatus(s.status),
    latencyMs: s.latencyMs ?? s.latency,
    version: s.version,
  }));
  const summary = raw.summary ?? {};
  return {
    status: normaliseStatus(raw.status),
    gateway: {
      name: raw.gateway?.name ?? "Polaris API",
      version: raw.gateway?.version,
      uptimeSeconds: raw.gateway?.uptime,
    },
    services,
    summary: {
      total: summary.total ?? services.length,
      healthy: summary.healthy ?? services.filter((s) => s.status === "ok").length,
      unhealthy:
        (summary.unhealthy ?? 0) + (summary.unreachable ?? 0) ||
        services.filter((s) => s.status !== "ok").length,
    },
    fetchedAt: new Date().toISOString(),
  };
}
